/* global performance */

/**
 * Progress reporting for the apply stages.
 *
 * Each step reports units completed out of a total known before the step
 * starts, so the bar is genuinely determinate rather than a guess.
 *
 * The estimate is deliberately per-step and coarse. Measured per-batch cost
 * inside a single step varied more than 10x on a real manuscript (identical
 * 25-insert batches ran 7.9s to 1m 20s), and rates are not comparable *between*
 * steps — a formatting-scan batch and a content-control insert differ by an
 * order of magnitude. So there is no honest whole-run estimate to give from one
 * step's rate, and even within a step an estimate from one or two samples would
 * swing wildly enough to be worse than showing nothing.
 */

export interface ApplyProgress {
  /** User-facing name of the step now running. */
  step: string;
  completed: number;
  total: number;
  /** Estimated ms left in this step; absent until the rate is worth trusting. */
  etaMs?: number;
}

export type ApplyProgressReporter = (progress: ApplyProgress) => void;

/**
 * Observations required before an estimate is shown. Counting *updates* rather
 * than units matters: a style-write step advances 500 paragraphs per batch, so a
 * unit threshold would be satisfied by the very first batch and estimate the
 * whole step from a single sample.
 */
const MIN_UPDATES_FOR_ETA = 3;

/** Tracks one step's completion and derives an estimate from its own rate. */
export class StepProgress {
  private completed = 0;
  private updates = 0;
  private readonly startedAt = performance.now();

  constructor(
    private readonly step: string,
    private readonly total: number,
    private readonly report?: ApplyProgressReporter
  ) {
    this.emit();
  }

  /** Report an absolute completed count. Callers all track their own index. */
  set(completed: number): void {
    this.completed = Math.max(0, Math.min(completed, this.total));
    this.updates += 1;
    this.emit();
  }

  /** Mark the step finished, so the bar reaches full before the next step. */
  done(): void {
    this.set(this.total);
  }

  private emit(): void {
    this.report?.({
      step: this.step,
      completed: this.completed,
      total: this.total,
      etaMs: this.etaMs(),
    });
  }

  private etaMs(): number | undefined {
    const remaining = this.total - this.completed;
    if (remaining <= 0 || this.completed <= 0 || this.updates < MIN_UPDATES_FOR_ETA) {
      return undefined;
    }
    const msPerUnit = (performance.now() - this.startedAt) / this.completed;
    return msPerUnit * remaining;
  }
}

/**
 * Render an estimate at a precision the underlying variance can support.
 * Seconds-level precision would imply confidence this measurement does not have.
 */
export function formatEta(ms: number): string {
  if (ms < 45_000) return "Under a minute remaining for this step";
  const minutes = Math.round(ms / 60_000);
  return `about ${minutes} min remaining`;
}
